import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Alert, Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Spinner, TextField, Typography } from '@glplus/glpweb-dsl';
import { RunnerPageHeading } from '../components/RunnerPageHeading';
import { Panel } from '../components/Panel';
import { RelationshipReport } from '../components/RelationshipReport';
import { RunnerAction } from '../components/RunnerAction';
import { runnerTheme } from '../styles/runnerTheme';
import { ORANGE_LINK_STYLE } from '../styles/colors';
import {
  runnerApi,
  RunnerApiError,
  type Board,
  type CustomerMatch,
  type DeliveriesBucket,
  type MatchStatus,
  type RelationshipBlock,
  type TenureBucket,
} from '../lib/runnerApiClient';

/**
 * §13 — "Your customers": the relationship half and the demand half, together.
 *
 * Matching is brokered and two-way. A customer asks for a Runner, or a Runner
 * offers to cover a customer, and the OTHER side says yes. So the only rows
 * that carry Accept / Decline are the ones the customer opened (`awaitingYou`);
 * a request this Runner opened waits on the customer and shows no buttons.
 * The API enforces the same rule with a 409 — the UI mirrors it so a Runner
 * is never shown a button that cannot work.
 *
 * 🔒 The board shows DEMAND, never PEOPLE (ruled 2026-09-02). Zones and counts,
 * bucketed, suppressed entirely under five households. A Runner meets a
 * customer only after both sides have said yes. There is no "offer to cover"
 * action on the board on purpose: a Runner cannot pick a household off a list.
 *
 * 🪤 A customer is rendered as their public id. runner-api genuinely knows
 * nothing else about them (isolation ruling 2026-08-24), and a made-up label
 * would be worse than an honest id.
 *
 * 🛑 Decline and End are final on the API (`ended` is terminal; a new
 * relationship is a new row). Both confirm first, with an optional reason.
 * Pause / Resume are reversible and act immediately.
 */

const GREEN = runnerTheme.green;
const NAVY = '#343F5B';

const STATUS_STYLE: Record<MatchStatus, { bg: string; fg: string }> = {
  requested: { bg: '#fef3c7', fg: '#b45309' },
  accepted: { bg: '#dbeafe', fg: '#1d4ed8' },
  active: { bg: '#dcfce7', fg: '#15803d' },
  paused: { bg: '#eef2f6', fg: '#4b5563' },
  ended: { bg: '#eef2f6', fg: '#5f6b7a' },
};

/** i18n keys cannot carry "1-6 months"; map the API's buckets to safe keys. */
const TENURE_KEY: Record<TenureBucket, string> = { new: 'new', '1-6 months': 'm1to6', '6+ months': 'm6plus' };
const DELIVERIES_KEY: Record<DeliveriesBucket, string> = { '0-5': 'd0to5', '6-20': 'd6to20', '20+': 'd20plus' };

function prettyDate(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(new Date(iso));
}

type Confirm = { kind: 'decline' | 'end' | 'block'; match: CustomerMatch } | { kind: 'unblock'; block: RelationshipBlock } | null;

export default function Customers({ view = 'all' }: { view?: 'all' | 'mine' | 'potential' }) {
  const showPotential = view !== 'mine';
  const showMine = view !== 'potential';
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';

  const [matches, setMatches] = useState<CustomerMatch[] | null>(null);
  const [matchesError, setMatchesError] = useState<string | null>(null);
  const [board, setBoard] = useState<Board | null>(null);
  const [boardError, setBoardError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [reason, setReason] = useState('');
  const [report, setReport] = useState<{ matchId: string; blocked: boolean } | null>(null);
  const [tab, setTab] = useState<'potential' | 'pending' | 'blocked'>('potential');
  const [blocks, setBlocks] = useState<RelationshipBlock[] | null>(null);
  const [blocksError, setBlocksError] = useState<string | null>(null);
  const es = locale.startsWith('es');
  const label = (en: string, spanish: string) => es ? spanish : en;

  const loadMatches = useCallback(async () => {
    try {
      setMatchesError(null);
      // 'all' so the page can show what ended, not just what is open — a
      // customer who was told "no" and one who was never asked are different
      // facts, and the Runner should be able to see their own history.
      const r = await runnerApi.matches('all');
      setMatches(r.matches);
    } catch (e) {
      setMatchesError(e instanceof RunnerApiError ? e.message : t('customers.loadFailed'));
    }
  }, [t]);

  const loadBoard = useCallback(async () => {
    if (!showPotential) return;
    setBoardError(null);
    try { setBoard(await runnerApi.board()); }
    catch (e) { setBoardError(e instanceof RunnerApiError ? e.message : t('customers.boardFailed')); }
  }, [showPotential, t]);

  const loadBlocks = useCallback(async () => {
    try { setBlocksError(null); setBlocks((await runnerApi.blocks()).blocks); }
    catch (e) { setBlocksError(e instanceof Error ? e.message : t('customers.loadFailed')); }
  }, [t]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try { await Promise.all([loadMatches(), loadBoard(), loadBlocks()]); }
    finally { setRefreshing(false); }
  }, [loadMatches, loadBoard, loadBlocks]);

  useEffect(() => { void refresh(); }, [refresh]);

  const rows = matches ?? [];
  const awaiting = useMemo(() => rows.filter((m) => m.status === 'requested' && m.awaitingYou), [rows]);
  const offered = useMemo(() => rows.filter((m) => m.status === 'requested' && !m.awaitingYou), [rows]);
  const current = useMemo(() => rows.filter((m) => m.status === 'accepted' || m.status === 'active' || m.status === 'paused'), [rows]);
  const past = useMemo(() => rows.filter((m) => m.status === 'ended'), [rows]);

  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      await refresh();
      return true;
    } catch (e) {
      // 409s from the API are written to be read by a Runner ("The match
      // changed while you were updating it.") — surface them verbatim.
      setActionError(e instanceof RunnerApiError ? e.message : t('customers.actionFailed'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  function closeConfirm() {
    if (busy) return;
    setConfirm(null);
    setReason('');
  }

  async function runConfirm() {
    if (!confirm) return;
    const decision = confirm;
    const r = reason.trim() || undefined;
    const succeeded = await act(() => decision.kind === 'unblock'
      ? runnerApi.blockMatch(decision.block.matchId!, false)
      : decision.kind === 'block' ? runnerApi.blockMatch(decision.match.id, true)
      : decision.kind === 'decline' ? runnerApi.declineMatch(decision.match.id, r) : runnerApi.endMatch(decision.match.id, r));
    if (succeeded) { setConfirm(null); setReason(''); }
  }

  if (report) return <RelationshipReport key={report.matchId} matchId={report.matchId} blocked={report.blocked} onClose={() => { setReport(null); void loadMatches(); void loadBlocks(); }} />;

  return (
    <Box data-testid="page-customers">
      <RunnerPageHeading
        title={t(view === 'mine' ? 'runnerCustomers.mineTitle' : view === 'potential' ? 'runnerHome.potentialTitle' : 'customers.title')}
        subtitle={t(view === 'mine' ? 'runnerCustomers.mineSubtitle' : view === 'potential' ? 'runnerCustomers.potentialSubtitle' : 'customers.subtitle')}
        topic="customers" onRefresh={() => void refresh()} refreshing={busy || refreshing}
      />

      {showPotential && <Box role="tablist" aria-label={label('Customer views', 'Vistas de clientes')} sx={{ display: 'flex', mb: 2, backgroundColor: '#fff', borderBottom: '1px solid #E2E6EE' }}>
        {(['potential', 'pending', 'blocked'] as const).map((key, index) => <Box key={key} component="button" role="tab" id={`customers-tab-${key}`} aria-controls={`customers-panel-${key}`} aria-selected={tab === key} onClick={() => setTab(key)} sx={{ flex: 1, minHeight: 56, p: '4px 12px', border: 0, borderBottom: tab === key ? `3px solid ${GREEN}` : '3px solid transparent', backgroundColor: '#fff', color: tab === key ? GREEN : NAVY, cursor: 'pointer', font: 'inherit', fontSize: 12, lineHeight: '18px' }}>
          {(es ? ['Clientes potenciales', 'Clientes pendientes', 'Bloqueados'] : ['Potential Customers', 'Pending Customers', 'Blocked'])[index]}<br />
          {key === 'blocked' ? blocksError ? '—' : blocks?.length ?? '…' : matchesError ? '—' : matches === null ? '…' : key === 'potential' ? awaiting.length : offered.length}
        </Box>)}
      </Box>}
      {showPotential && tab === 'blocked' && <Box role="tabpanel" id="customers-panel-blocked" aria-labelledby="customers-tab-blocked" sx={{ mb: 2 }}>
        <Panel title={label('Blocked customers', 'Clientes bloqueados')} accent={GREEN}>
          <Typography sx={{ mb: 2 }}>{label('Only customers you blocked appear here. Unblocking does not restore a relationship. Existing orders stay on record; contact support if needed.', 'Aquí solo aparecen los clientes que bloqueaste. Desbloquear no restablece la relación. Los pedidos existentes se conservan; contacta a soporte si necesitas ayuda.')}</Typography>
          {blocksError ? <Alert severity="error">{blocksError}</Alert> : blocks === null ? <Spinner /> : blocks.length === 0 ? <Typography>{label('No blocked customers.', 'No hay clientes bloqueados.')}</Typography> : blocks.map(block => <Box key={block.id} sx={{ borderTop: '1px solid #E2E6EE', py: 2 }}>
            <Typography>{matches?.find(match => match.customerGlpUserId === block.customerGlpUserId)?.customerDisplayName || t('customers.customer')}</Typography>
            <Typography sx={{ fontSize: '13px', color: '#5f6b7a' }}>{block.customerGlpUserId}</Typography>
            {block.matchId ? <RunnerAction ariaLabel={label('Report a problem', 'Reportar un problema')} buttonText={label('Report a problem', 'Reportar un problema')} topic="customers" disabled={busy} onClick={() => setReport({ matchId: block.matchId!, blocked: true })} /> : null}
            {block.matchId ? <RunnerAction buttonText={label('Unblock', 'Desbloquear')} ariaLabel={label('Unblock', 'Desbloquear')} topic="customers" disabled={busy} onClick={() => setConfirm({ kind: 'unblock', block })} /> : <RunnerAction buttonText={label('Contact support', 'Contactar a soporte')} ariaLabel={label('Contact support', 'Contactar a soporte')} href="/help" topic="customers" />}
          </Box>)}
        </Panel>
      </Box>}
      {actionError && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{actionError}</Alert>
        </Box>
      )}

      <Box role={showPotential ? "tabpanel" : undefined} id={showPotential ? `customers-panel-${tab}` : undefined} aria-labelledby={showPotential ? `customers-tab-${tab}` : undefined} sx={{ display: showPotential && tab === 'blocked' ? 'none' : 'grid', gridTemplateColumns: view === 'all' ? { xs: '1fr', md: '1.6fr 1fr' } : '1fr', gap: 2.5, alignItems: 'start' }}>
        <Box sx={{ display: 'grid', gap: 2.5 }}>
          {matchesError ? (
            // 🛑 NOT "no customers yet". A failed read is not an empty book.
            <Alert severity="error" data-testid="customers-error">{matchesError}</Alert>
          ) : matches === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
              <Spinner />
            </Box>
          ) : (
            <>
              {showPotential && (view === 'all' || tab === 'potential') && <Panel title={t('customers.requestsTitle')} accent={GREEN} dataTestId="customers-requests">
                {awaiting.length === 0 ? (
                  <Typography data-testid="customers-requests-empty" sx={{ color: '#4b5563', fontSize: '14px' }}>{t('customers.requestsEmpty')}</Typography>
                ) : (
                  <Box sx={{ display: 'grid', gap: 1.25 }}>
                    {awaiting.map((m) => (
                      <MatchCard key={m.id} m={m} locale={locale}>
                        <RunnerAction ariaLabel={t('customers.accept')} buttonText={t('customers.accept')} appearance="primary" size="small" disabled={busy} onClick={() => void act(() => runnerApi.acceptMatch(m.id))} />
                        <RunnerAction ariaLabel={t('customers.decline')} buttonText={t('customers.decline')} topic="customers" size="small" disabled={busy} onClick={() => setConfirm({ kind: 'decline', match: m })} />
                      </MatchCard>
                    ))}
                  </Box>
                )}
              </Panel>}

              {showPotential && (view === 'all' ? offered.length > 0 : tab === 'pending') && (
                <Panel title={t('customers.offeredTitle')} accent={GREEN} dataTestId="customers-offered">
                  {offered.length === 0 && <Typography>{label('No pending customers.', 'No hay clientes pendientes.')}</Typography>}
                  <Box sx={{ display: 'grid', gap: 1.25 }}>
                    {offered.map((m) => (
                      <MatchCard key={m.id} m={m} locale={locale}>
                        <RunnerAction ariaLabel={t('customers.withdraw')} buttonText={t('customers.withdraw')} topic="customers" size="small" disabled={busy} onClick={() => setConfirm({ kind: 'end', match: m })} />
                      </MatchCard>
                    ))}
                  </Box>
                </Panel>
              )}

              {showMine && <Panel title={t('customers.currentTitle')} accent={GREEN} dataTestId="customers-current">
                {current.length === 0 ? (
                  <Typography data-testid="customers-current-empty" sx={{ color: '#4b5563', fontSize: '14px' }}>{t('customers.currentEmpty')}</Typography>
                ) : (
                  <Box sx={{ display: 'grid', gap: 1.25 }}>
                    {current.map((m) => (
                      <MatchCard key={m.id} m={m} locale={locale}>
                        {m.status === 'paused' ? (
                          <RunnerAction ariaLabel={t('customers.resume')} buttonText={t('customers.resume')} appearance="primary" size="small" disabled={busy} onClick={() => void act(() => runnerApi.resumeMatch(m.id))} />
                        ) : (
                          <RunnerAction ariaLabel={t('customers.pause')} buttonText={t('customers.pause')} topic="customers" size="small" disabled={busy} onClick={() => void act(() => runnerApi.pauseMatch(m.id))} />
                        )}
                        <RunnerAction ariaLabel={t('customers.end')} buttonText={t('customers.end')} topic="customers" size="small" disabled={busy} onClick={() => setConfirm({ kind: 'end', match: m })} />
                        <RunnerAction ariaLabel={label('Block customer', 'Bloquear cliente')} buttonText={label('Block customer', 'Bloquear cliente')} topic="customers" size="small" disabled={busy} onClick={() => setConfirm({ kind: 'block', match: m })} />
                        <RunnerAction ariaLabel={label('Report a problem', 'Reportar un problema')} buttonText={label('Report a problem', 'Reportar un problema')} topic="customers" disabled={busy} onClick={() => setReport({ matchId: m.id, blocked: blocks?.some(block => block.customerGlpUserId === m.customerGlpUserId) ?? false })} />
                      </MatchCard>
                    ))}
                  </Box>
                )}
              </Panel>}

              {showMine && past.length > 0 && (
                <Panel title={t('customers.pastTitle')} accent="#5f6b7a" dataTestId="customers-past">
                  <Box sx={{ display: 'grid', gap: 0.5 }}>
                    {past.map((m) => (
                      <Typography key={m.id} sx={{ color: '#5f6b7a', fontSize: '13px' }}>
                        {m.customerDisplayName || t('customers.customer')} · <Box component="span" sx={{ fontFamily: 'monospace' }}>{m.customerGlpUserId}</Box> · {m.zone} ·{' '}
                        {m.endedAt ? t('customers.endedOn', { date: prettyDate(m.endedAt, locale) }) : t('customers.status.ended')}
                        {m.endedReason ? ` · ${m.endedReason}` : ''}
                      </Typography>
                    ))}
                  </Box>
                </Panel>
              )}
            </>
          )}
        </Box>

        {showPotential && (view === 'all' || tab === 'potential') && <BoardPanel board={board} error={boardError} />}
      </Box>

      <Dialog open={confirm !== null} onClose={closeConfirm} ariaLabelledBy="customers-confirm-title" maxWidth="xs" fullWidth>
        <DialogTitle id="customers-confirm-title">{confirm?.kind === 'block' ? label('Block customer?', '¿Bloquear cliente?') : confirm?.kind === 'unblock' ? label('Unblock customer?', '¿Desbloquear cliente?') : confirm?.kind === 'decline' ? t('customers.declineTitle') : t('customers.endTitle')}</DialogTitle>
        <DialogContent>
          {actionError && <Alert severity="error">{actionError}</Alert>}
          <Typography sx={{ color: '#4b5563', fontSize: '14px', mb: 2 }}>
            {confirm?.kind === 'block' ? label('This ends the relationship and prevents future matching and contact. The customer will not be notified. Existing orders are retained; contact support about an active delivery.', 'Esto termina la relación e impide futuras conexiones y contacto. No se notificará al cliente. Los pedidos existentes se conservan; contacta a soporte sobre una entrega activa.') : confirm?.kind === 'unblock' ? label('Future matching will be possible again. Your former relationship will not be restored.', 'Las conexiones futuras volverán a ser posibles. La relación anterior no se restablecerá.') : confirm?.kind === 'decline' ? t('customers.declineBody') : t('customers.endBody')}
          </Typography>
          {(confirm?.kind === 'decline' || confirm?.kind === 'end') && <TextField
            label={t('customers.reasonLabel')}
            ariaLabel={t('customers.reasonLabel')}
            size="small"
            fullWidth
            value={reason}
            disabled={busy}
            placeholder={t('customers.reasonPlaceholder')}
            // The API caps reason at 60; clip here so the 400 never happens.
            onChange={(e) => setReason(e.target.value.slice(0, 60))}
          />}
        </DialogContent>
        <DialogActions>
          <RunnerAction ariaLabel={t('customers.keep')} buttonText={t('customers.keep')} topic="customers" size="small" disabled={busy} onClick={closeConfirm} />
          <RunnerAction
            ariaLabel={confirm?.kind === 'block' ? label('Block customer', 'Bloquear cliente') : confirm?.kind === 'unblock' ? label('Unblock', 'Desbloquear') : confirm?.kind === 'decline' ? t('customers.confirmDecline') : t('customers.confirmEnd')}
            buttonText={busy ? t('customers.working') : confirm?.kind === 'block' ? label('Block customer', 'Bloquear cliente') : confirm?.kind === 'unblock' ? label('Unblock', 'Desbloquear') : confirm?.kind === 'decline' ? t('customers.confirmDecline') : t('customers.confirmEnd')}
            appearance="primary"
            size="small"
            disabled={busy}
            onClick={() => void runConfirm()}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function MatchCard({ m, locale, children }: { m: CustomerMatch; locale: string; children?: React.ReactNode }) {
  const { t } = useTranslation();
  const style = STATUS_STYLE[m.status];
  const when =
    m.status === 'requested'
      ? m.initiatedBy === 'customer'
        ? t('customers.askedByCustomer', { date: prettyDate(m.requestedAt, locale) })
        : t('customers.youOffered', { date: prettyDate(m.requestedAt, locale) })
      : m.acceptedAt
        ? t('customers.since', { date: prettyDate(m.acceptedAt, locale) })
        : '';
  return (
    <Box data-testid={`customer-match-${m.id}`} data-status={m.status} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, px: 1.75, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1f2937' }}>
            {m.customerDisplayName || t('customers.customer')} · {m.zone}
          </Typography>
          <Chip label={t(`customers.status.${m.status}`)} size="small" backgroundColor={style.bg} textColor={style.fg} fontWeight={700} />
          {m.isPrimary && m.status !== 'requested' && (
            <Typography sx={{ color: '#5f6b7a', fontSize: '12px' }}>{t('customers.primary')}</Typography>
          )}
        </Box>
        <Typography sx={{ color: '#5f6b7a', fontSize: '13px' }}>
          {when}
          {when ? ' · ' : ''}
          <Box component="span" sx={{ fontFamily: 'monospace', color: '#5f6b7a' }}>{m.customerGlpUserId}</Box>
        </Typography>
      </Box>
      {children && <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>{children}</Box>}
    </Box>
  );
}

function BoardPanel({ board, error }: { board: Board | null; error: string | null }) {
  const { t } = useTranslation();
  return (
    <Panel title={t('customers.boardTitle')} accent={NAVY} dataTestId="customers-board">
      <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mb: 1.5 }}>{t('customers.boardNote')}</Typography>
      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : board === null ? (
        <Spinner size={20} />
      ) : board.zonesSearched.length === 0 ? (
        <Typography sx={{ color: '#4b5563', fontSize: '14px' }}>
          {t('customers.boardNoWindows')}{' '}
          <Link to="/schedule" style={ORANGE_LINK_STYLE}>{t('customers.boardSetSchedule')}</Link>
        </Typography>
      ) : board.zones.length === 0 ? (
        <Typography sx={{ color: '#4b5563', fontSize: '14px' }}>{t('customers.boardEmpty', { zones: board.zonesSearched.join(', ') })}</Typography>
      ) : (
        <Box sx={{ display: 'grid', gap: 1.25 }}>
          {board.zones.map((z) => (
            <Box key={z.zone} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, px: 1.75, py: 1.25 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                <Typography sx={{ fontWeight: 800, fontSize: '16px', color: '#1f2937' }}>{z.zone}</Typography>
                <Typography sx={{ fontWeight: 800, fontSize: '14px', color: GREEN }}>
                  {z.openings === 1 ? t('customers.oneOpening') : t('customers.openings', { count: z.openings })}
                </Typography>
              </Box>
              <Typography sx={{ color: '#5f6b7a', fontSize: '13px', mt: 0.5 }}>
                {[
                  z.typicalWindow ? t('customers.typicalWindow', { window: z.typicalWindow }) : null,
                  z.languages.length ? t('customers.languages', { list: z.languages.join(', ') }) : null,
                  t(`customers.tenure.${TENURE_KEY[z.tenure]}`),
                  t(`customers.deliveries.${DELIVERIES_KEY[z.deliveries]}`),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {board && board.suppressedZoneCount > 0 && (
        <Typography sx={{ color: '#5f6b7a', fontSize: '12px', mt: 1.5 }}>{t('customers.suppressed', { count: board.suppressedZoneCount })}</Typography>
      )}
    </Panel>
  );
}
